import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchNotifications, notificationReceived } from '../store/slices/notificationSlice';

/**
 * Keeps the tab-bar badge honest while the app is open.
 *
 * Three sources, because no one of them is sufficient:
 *
 *   1. A MESSAGE FROM THE SERVICE WORKER when a push arrives. This is the one that
 *      makes the badge move the instant something happens — but only on a device where
 *      push is actually working, which is never guaranteed.
 *   2. A REFETCH WHEN THE APP COMES BACK TO THE FOREGROUND. A phone in a pocket
 *      suspends timers; this is what makes the badge right the moment it is looked at,
 *      which is the only moment it matters.
 *   3. A SLOW POLL as the floor. Covers the phone left open on a bench all afternoon
 *      with push denied.
 *
 * ⚠️ The poll is deliberately slow. It is a backstop, not the mechanism: a fast poll
 * from every phone in every workshop would cost far more than it buys, and the two
 * signals above already cover the cases anyone notices.
 */

/** Slow on purpose — see above. */
const POLL_INTERVAL_MS = 2 * 60 * 1000;

const useNotificationSync = (enabled) => {
    const dispatch = useDispatch();

    useEffect(() => {
        // A fleet customer has no staff identity, so the endpoint would 400 for them.
        if (!enabled) return undefined;

        dispatch(fetchNotifications());

        const onVisible = () => {
            if (document.visibilityState === 'visible') dispatch(fetchNotifications());
        };
        const onServiceWorkerMessage = (event) => {
            if (event.data?.type === 'push-notification') {
                // Bump immediately so the badge responds, then reconcile with the
                // server — the push may be one of several, and the count is the
                // server's to state.
                dispatch(notificationReceived());
                dispatch(fetchNotifications());
            }
        };

        document.addEventListener('visibilitychange', onVisible);
        navigator.serviceWorker?.addEventListener?.('message', onServiceWorkerMessage);
        const timer = setInterval(() => dispatch(fetchNotifications()), POLL_INTERVAL_MS);

        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            navigator.serviceWorker?.removeEventListener?.('message', onServiceWorkerMessage);
            clearInterval(timer);
        };
    }, [dispatch, enabled]);
};

export default useNotificationSync;
